import { analytics } from "@/app/firebase";
import { CloudflareAsset, type DownloadType } from "@/shared/common/enums";
import type { IError, IStatementAction } from "@/shared/common/types";
import { CloudFlareImage, PageHeader, PageWidgetTemplate } from "@/shared/components";
import { htmlToBlobUrl } from "@/shared/helpers";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { Heading } from "@ab.uitools/ui-library/components/Heading";
import { Menu } from "@ab.uitools/ui-library/components/Menu";
import { Spinner } from "@ab.uitools/ui-library/components/Spinner";
import { IconArrowDownload } from "@ab.uitools/ui-library/components/SVGIcons";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";
import { logEvent } from "firebase/analytics";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  onDownload: (exportFormat: DownloadType) => void;
  isFetching: boolean;
  isError: boolean;
  isDownloading: boolean;
  error?: IError;
  actions?: IStatementAction[]
  statement?: string;
  refetch: () => void;
  children?: ReactNode;
}

export const Statement = ({
  onDownload,
  isDownloading,
  isError,
  isFetching,
  error,
  actions,
  refetch,
  statement,
  children,
}: Props) => {
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeSrc, setIframeSrc] = useState<string>("");
  const [ref, setRef] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState<boolean>(false);

  const onToggleDownloadMenu = () => {
    setOpen((prev) => !prev);
  };

  const handleDownload = (exportFormat: DownloadType) => {
    const analyticId = "download-statement";
    const label = "Download statement";
    onDownload(exportFormat);
    logEvent(analytics, analyticId, {
      content_type: label,
      analyticId,
    });
  };

  const actionsMemo = useMemo(() => {
    return actions?.map((action) => ({
      ...action,
      value: action.type,
      handler: () => handleDownload(action.type),
    }));
  }, [actions]);

  useEffect(() => {
    if (statement) {
      const { url, revoke } = htmlToBlobUrl(statement);
      setIframeSrc(url);
      return revoke;
    }
  }, [statement]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      const doc = iframe.contentWindow?.document;
      if (doc) {
        iframe.style.height = doc.documentElement.scrollHeight + "px";
        doc.body.style.overflowY = "hidden";
      }
    };

    iframe.addEventListener("load", handleLoad);
    return () => iframe.removeEventListener("load", handleLoad);
  }, [iframeRef.current, isFetching]);

  return (
    <>
      <PageHeader>
        <Button
          refHandler={setRef}
          type="secondary"
          className="flexbox align-items--center"
          onClick={onToggleDownloadMenu}
          disabled={isDownloading}
          data-testid="toggle-download-statement-menu"
          data-ga-id="toggle-download-statement-menu"
          data-ga-action="Toggle download statement menu"
          buttonText={
            <div className="flexbox align-items--center">
              {isDownloading ? (
                <Spinner className="spinner-icon" isFullScreen={false} size="small"/>
              ) : <IconArrowDownload/>}
              <Text className="pl-6">{t("general.download")}</Text>
            </div>
          }
          iconProps={{
            Component: (props: ISVGIconProps) => (
              <CloudFlareImage
                {...props}
                path={
                  open ?
                    CloudflareAsset.ArrowShortUp :
                    CloudflareAsset.ArrowShortDown
                }
              />
            ),
            alignment: "right",
          }}
        />
        <Menu
          position={Positions.BOTTOM_LEFT}
          onClose={() => setOpen(false)}
          isOpen={open}
          additionalRef={ref}
          parentRef={ref}
          menuItems={actionsMemo}
        />
      </PageHeader>
      <div
        className="full-width flexbox justify-content--center h-fit-content page-body statement-page">
        <div className="widget-wrapper">
          <div className="full-width mb-20">
            <Heading className="content-title" size="xsmall" weight="bolder">
              {t("general.statement.title")}
            </Heading>
          </div>
          {children}
          <PageWidgetTemplate
            isEmpty={!statement}
            isFetching={isFetching}
            isError={isError}
            error={error as IError}
            refetch={refetch}
          >
            <div className="p-32 statement">
              <iframe
                ref={iframeRef}
                src={iframeSrc}
                className="statement-iframe"
              />
            </div>
          </PageWidgetTemplate>
        </div>
      </div>
    </>
  );
};
